const isNative = '__TAURI__' in window; // true if iOS or Android

export const DeviceOrientationEventWithPermission = DeviceOrientationEvent as typeof DeviceOrientationEvent & {
	requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
};

export async function asyncWatchPosition(
	cb: (pos: GeolocationPosition | null, err?: GeolocationPositionError) => void,
	options: PositionOptions = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
) {
	if (isNative) throw new Error('Cannot run in native');

	return navigator.geolocation.watchPosition(
		cb,
		(err) => {
			cb(null, err);
		},
		options,
	);
}

export function asyncGetCurrentPosition(options: PositionOptions = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }) {
	return new Promise<GeolocationPosition>((resolve, reject) => {
		if (isNative) return reject('Cannot run in native');
		navigator.geolocation.getCurrentPosition(
			(pos) => resolve(pos),
			(err) => reject(err),
			options,
		);
	});
}

const callbacks = new Set<(heading: number | null) => void>();
let listening = false;

function handleOrientation(e: DeviceOrientationEvent & { webkitCompassHeading?: number }) {
	let headingDeg: number | null = null;
	if (e.webkitCompassHeading) {
		const orientation = {
			'portrait-primary': 0,
			'portrait-secondary': 180, //Upside down
			'landscape-primary': 90,
			'landscape-secondary': 270,
		}[window.screen.orientation?.type];
		headingDeg = e.webkitCompassHeading + orientation;
	} else if (e.alpha !== null) {
		headingDeg = 360 - e.alpha;
		const orientation = window.screen.orientation?.angle || 0;
		headingDeg = (headingDeg + orientation) % 360;
	}

	callbacks.forEach((cb) => cb(headingDeg));
}

export function subscribeOrientationListener(cb: (heading: number | null) => void) {
	callbacks.add(cb);
	if (!listening) {
		try {
			const eventToListen = iSiOS() ? 'deviceorientation' : 'deviceorientationabsolute';
			window.addEventListener(eventToListen, handleOrientation as EventListener);
			listening = true;
		} catch (err) {
			console.warn(err);
		}
	}
}

export function unsubscribeOrientationListener(cb: (heading: number | null) => void) {
	callbacks.delete(cb);
	if (callbacks.size === 0 && listening) {
		try {
			const eventToRemove = iSiOS() ? 'deviceorientation' : 'deviceorientationabsolute';
			window.removeEventListener(eventToRemove, handleOrientation as EventListener);
			listening = false;
		} catch (err) {
			console.warn(err);
		}
	}
}

function iSiOS() {
	return (
		['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
		// iPad on iOS 13 detection
		(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
	);
}
